<template>
  <div ref="datePickerContainer" class="datePickerContainer relative">
    <section class="d-flex w100">
      <div class="col align-start w100">
        <label class="black--text">Date</label>
        <div class="row w100 relative">
          <input ref="button" v-model="selected" maxlength="0" type="text" :style="inputStyling" :disabled="!noedit" data-lpignore="true" class="capital" @keydown.delete="(e) => e.preventDefault()" @click="showPicker" @keydown.enter="showPicker" @keydown.space="showPicker" @keydown.esc="onBlur">
          <button v-if="noedit" tabindex="-1" type="button" class="absolute p-right p-center-y px-15 center element" :style="boxStyling" @click="showPicker">
            <s-icon height="16" :color="show ? 'white' : 'grey'">
              calendar-week
            </s-icon>
          </button>
        </div>
      </div>

    </section>

    <Teleport to="body" :disabled="!isMobile">
    <transition name="dropdown">
      <div v-if="show" ref="datepickerEl" v-hotkey="{ esc: onBlur }" class="datepicker noSelect">
        <FocusLoop>
          <div class="d-flex space-between capital">
            <div class="row align-center">
              {{ dayjs(date).locale(lang).format("MMM") }} {{ dayjs(date).format("YYYY") }}
            </div>
            <div class="row gap-10">
              <button type="button" class="center chevron ah-30 ratio" @click="decrement">
                <s-icon color="black">
                  chevron-left
                </s-icon>
              </button>
              <button type="button" class="center chevron ah-30 ratio" @click="increment">
                <s-icon color="black">
                  chevron-right
                </s-icon>
              </button>
            </div>
          </div>
          <div class="headings capital">
            <div v-for="item in daysOfWeek" :key="item.idx">
              {{ item }}
            </div>
          </div>
          <div class="grid-container">
            <div v-for="item in monthRange" :key="item.idx" class="center">
              <button v-if="item.month() + 1 == date.format('M')" class="opacity-60 today-oval center oval" type="button" :class="{
                shade4: today == item.format('DD/MMM/YYYY'),
                selected:
                  absDate.format('MMM D, YYYY') == item.format('MMM D, YYYY'),
              }" @click="selectDate(item)">
                {{ item.format("D") }}
              </button>
            </div>
          </div>
        </FocusLoop>
      </div>
    </transition>
    </Teleport>
    <Teleport to="body">
      <transition name="picker-fade">
        <div v-if="show" class="picker-backdrop" @click="onBlur" />
      </transition>
    </Teleport>
  </div>
</template>

<script setup>
import dayjs from "dayjs";
import localData from "dayjs/plugin/localeData";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
const { height, prefill, noedit } = defineProps(["height", "prefill", "noedit"]);

const emit = defineEmits();

dayjs.extend(localData);
dayjs.extend(localizedFormat);

const store = useStore();
const router = useRouter();

const lang = computed(() => store.state.user.lang);
const daysOfWeek = computed(() => dayjs().locale(lang.value).localeData().weekdaysMin());


let show = ref(false);
let absDate = ref(null);
let today = ref(null);
let button = ref(null);
let date = ref(null);
let selected = ref(null);
let datePickerContainer = ref(null);
let datepickerEl = ref(null);
let monthRange = ref([]);
const isMobile = ref(window.innerWidth <= 576);
const onResizeMobile = () => { isMobile.value = window.innerWidth <= 576; };

const inputStyling = computed(() => {
  let ob = {};
  if (height) ob.height = height + "px";
  if (!noedit) ob.backgroundColor = "#00000000";
  if (show.value) ob.backgroundColor = "var(--white)";
  return ob;
});

const boxStyling = computed(() => {
  let ob = {};
  if (height) ob.height = height + "px";
  if (height) ob.minWidth = height + "px";
  if (show.value) ob.backgroundColor = "var(--primary)";
  return ob;
});

const generateCal = (date) => {
  monthRange.value = [];
  let day = date.startOf("month").startOf("week");
  for (let x = 0; x < 42; x++) {
    monthRange.value.push(day.add(x, "day"));
  }
};

const increment = () => {
  date.value = date.value.add(1, "month");
  generateCal(date.value);
};

const decrement = () => {
  date.value = date.value.subtract(1, "month");
  generateCal(date.value);
};

const selectDate = (val) => {
  selected.value = val.locale(lang.value).format("ll");
  absDate.value = val;
  emit("changeDate", val);
  show.value = false;
  onBlur();
  button.value.focus();
};

const showPicker = () => {
  if (!noedit) return;
  show.value = !show.value;

};

const listen = (event) => {
  const el = datePickerContainer.value;
  if (!el.contains(event.target) && !datepickerEl.value?.contains(event.target)) {
    show.value = false;
    onBlur();
  }
};

const onBlur = () => {
  show.value = false;
};

const init = () => {
  date.value = dayjs();
  today.value = dayjs().format("DD/MMM/YYYY");
  selected.value = dayjs().locale(lang.value).format("ll");
  absDate.value = dayjs();
  if (prefill) {
    selected.value = dayjs(prefill, "MMM D, YYYY");
    selected.value = selected.value.locale(lang.value).format("ll");
    date.value = dayjs(prefill, "MMM D, YYYY");
    absDate.value = dayjs(prefill, "MMM D, YYYY");
  }
  generateCal(date.value);

  if (!noedit) return;
  emit("changeDate", dayjs(absDate.value));
};

watch(router.currentRoute, () => {
  init();
});

watch(lang, () => {
  init();
});

onMounted(() => {
  document.body.addEventListener("click", listen);
  window.addEventListener("resize", onResizeMobile);
  init();
});

onUnmounted(() => {
  document.body.removeEventListener("click", listen);
  window.removeEventListener("resize", onResizeMobile);
});

defineExpose({ showPicker, close: onBlur });

</script>


<style scoped>
.datePickerContainer {
  width: 100%;
}

.element {
  user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  border-radius: 8px;
}

.datepicker {
  width: 300px;
  box-shadow:
    0 18px 44px -16px rgba(16, 24, 39, 0.35),
    0 4px 12px -6px rgba(16, 24, 39, 0.12);
  background-color: var(--white);
  color: var(--shade7);
  padding: 16px;
  box-sizing: border-box;
  position: absolute;
  z-index: 101;
  right: 0;
  bottom: 56px;
  border: 1px solid var(--shade4);
  border-radius: 16px;
  font-size: 15px;
  font-weight: 600;
}

.datepicker .space-between {
  align-items: center;
}

.headings {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  color: var(--shade5);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  margin-top: 16px;
  text-align: center;
}

.grid-container {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: repeat(6, 1fr);
  height: 234px;
  margin-top: 6px;
}

.line {
  width: 0;
  height: 1px;
  background-color: var(--primary);
  transition: all ease 0.2s;
}

.today-oval {
  height: 86%;
  width: 86%;
  border-radius: 50%;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--shade7);
  opacity: 1;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.oval:hover,
.oval:focus {
  background-color: color-mix(in srgb, var(--primary) 12%, var(--white));
  border-radius: 50%;
  height: 86%;
  width: 86%;
  opacity: 1;
  color: var(--primary);
}

/* today */
.today-oval.shade4 {
  background-color: var(--shade2);
  color: var(--shade7);
  font-weight: 600;
}

.chevron {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  transition: background-color 0.15s ease;
}

.chevron:hover {
  background-color: var(--shade2);
}

.chevron:focus,
.chevron:active {
  background-color: var(--shade3);
  border-radius: 10px;
}

.selected,
.today-oval.selected {
  background-color: var(--primary);
  border: none;
  color: var(--white);
  border-radius: 50%;
  height: 86%;
  width: 86%;
  font-weight: 700;
  opacity: 1;
  box-shadow: 0 4px 12px -4px color-mix(in srgb, var(--primary) 60%, transparent);
}

.selected:hover,
.today-oval.selected:hover {
  background-color: var(--primary);
  color: var(--white);
}

input {
  background-color: var(--shade3);
  border: 1.5px solid var(--shade4);
  font-size: 14px;
  color: var(--black);
  padding: 12px 19px 12px 19px;
  width: 100%;
  box-sizing: border-box;
  caret-color: transparent;
  -webkit-appearance: none;
  border-radius: 0;
  cursor: pointer;
  user-select: none;
  pointer-events: auto;
  border-radius: 8px;
  transition: all 0.3s ease;
 height: 50px;
}

input:disabled {
  cursor: default;
}

input:hover {
  background-color: var(--shade3);
}

input:focus {
  background-color: var(--white);
  border-color: var(--primary);
  color: var(--black);
  font-size: 14px;
  outline: none;
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--primary) 12%, transparent);
}

input::selection {
  background: none;
}

button {
  color: var(--black);
}

/* dimmed backdrop behind the picker on mobile (centered modal) */
.picker-backdrop {
  display: none;
}

.picker-fade-enter-active,
.picker-fade-leave-active {
  transition: opacity 0.2s ease;
}

.picker-fade-enter-from,
.picker-fade-leave-to {
  opacity: 0;
}

@media only screen and (max-width: 576px) {
  .datepicker {
    position: fixed;
    top: 50%;
    left: 50%;
    right: auto;
    bottom: auto;
    transform: translate(-50%, -50%);
    z-index: 1001;
  }

  .picker-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(16, 24, 39, 0.45);
    z-index: 1000;
  }
}
</style>